import React, { useState } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useNavigate } from "react-router-dom";
import config from "../../config";
import terms from "../../config/terminology";

const DonatorSignup = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    savePaymentMethod: true,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [signupSuccess, setSignupSuccess] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Create donator account with password
      const donatorResponse = await fetch(`${config.API_URL}/donators/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          password: formData.password,
        }),
      });

      if (!donatorResponse.ok) {
        const errorData = await donatorResponse.json();
        throw new Error(errorData.detail || "Failed to create donator account");
      }

      const donatorData = await donatorResponse.json();

      if (formData.savePaymentMethod && stripe && elements) {
        // Step 2: Create payment method
        const { error, paymentMethod } = await stripe.createPaymentMethod({
          type: "card",
          card: elements.getElement(CardElement),
          billing_details: {
            name: `${formData.firstName} ${formData.lastName}`,
            email: formData.email,
          },
        });

        if (error) {
          throw new Error(error.message);
        }

        // Step 3: Save payment method to donator
        const saveResponse = await fetch(
          `${config.API_URL}/donators/${donatorData.id}/payment-methods`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ payment_method_id: paymentMethod.id }),
          }
        );

        if (!saveResponse.ok) {
          throw new Error("Failed to save payment method");
        }
      }

      // Success - show verification message
      setSignupSuccess(true);
    } catch (error) {
      console.error("Signup error:", error);
      setErrors({ submit: error.message });
    } finally {
      setLoading(false);
    }
  };

  // Show success screen after signup
  if (signupSuccess) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
        <div className="bg-gray-900 p-8 rounded-lg w-full max-w-md border border-green-500 text-center">
          <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">✓</span>
          </div>
          <h2 className="text-2xl font-bold mb-4">Check Your Email!</h2>
          <p className="text-gray-400 mb-6">
            We've sent a verification link to <strong className="text-white">{formData.email}</strong>
          </p>
          <p className="text-gray-400 mb-6">
            Please click the link in the email to verify your account before logging in.
          </p>
          <button
            onClick={() => navigate("/donator-login")}
            className="bg-blue-500 hover:bg-blue-600 px-6 py-2 rounded-full font-semibold transition"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
      <div className="bg-gray-900 p-8 rounded-lg w-full max-w-md border border-blue-500">
        <h2 className="text-2xl font-bold text-center mb-6">
          Create {terms.Donator} Account
        </h2>
        <p className="text-gray-400 text-center mb-6">
          Sign up to save your payment information and make {terms.donating} easier
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                First Name
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className="w-full p-2 rounded bg-gray-800 border border-gray-700 focus:border-blue-500 focus:outline-none"
                placeholder="John"
              />
              {errors.firstName && (
                <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Last Name
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className="w-full p-2 rounded bg-gray-800 border border-gray-700 focus:border-blue-500 focus:outline-none"
                placeholder="Doe"
              />
              {errors.lastName && (
                <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="w-full p-2 rounded bg-gray-800 border border-gray-700 focus:border-blue-500 focus:outline-none"
              placeholder="john@example.com"
            />
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className="w-full p-2 rounded bg-gray-800 border border-gray-700 focus:border-blue-500 focus:outline-none"
              placeholder="Minimum 8 characters"
            />
            {errors.password && (
              <p className="text-red-500 text-xs mt-1">{errors.password}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Confirm Password</label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className="w-full p-2 rounded bg-gray-800 border border-gray-700 focus:border-blue-500 focus:outline-none"
              placeholder="Re-enter password"
            />
            {errors.confirmPassword && (
              <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>
            )}
          </div>

          <div className="border-t border-gray-700 pt-4">
            <label className="flex items-center space-x-2 mb-4">
              <input
                type="checkbox"
                name="savePaymentMethod"
                checked={formData.savePaymentMethod}
                onChange={handleInputChange}
                className="rounded border-gray-600 text-blue-500 focus:ring-blue-500"
              />
              <span className="text-sm">
                Save payment method for faster {terms.donations}
              </span>
            </label>

            {formData.savePaymentMethod && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  Payment Information
                </label>
                <div className="p-4 rounded bg-white">
                  <CardElement
                    options={{
                      style: {
                        base: {
                          fontSize: "16px",
                          color: "#000",
                          "::placeholder": {
                            color: "#9ea7c4",
                          },
                        },
                      },
                    }}
                  />
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Your payment information is securely stored with Stripe
                </p>
              </div>
            )}
          </div>

          {errors.submit && (
            <div className="bg-red-500 bg-opacity-10 border border-red-500 p-3 rounded">
              <p className="text-red-500 text-sm">{errors.submit}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || (formData.savePaymentMethod && !stripe)}
            className="w-full py-3 bg-blue-500 rounded-lg font-semibold hover:bg-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Creating Account..." : "Create Account"}
          </button>

          <p className="text-xs text-gray-400 text-center mt-4">
            By signing up, you agree to our Terms of Service and Privacy Policy.
          </p>

          <p className="text-center mt-4">
            <button
              type="button"
              onClick={() => navigate("/donator-dashboard")}
              className="text-blue-400 hover:text-blue-300 text-sm"
            >
              Continue as guest
            </button>
          </p>
        </form>
      </div>
    </div>
  );
};

export default DonatorSignup;